import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react'

import { InviteMemberForm, TeamMemberRole } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { RolesSelector } from '@/modules/creators/ui/RoleSelector'
import { emailSchema } from '@/types/schemas.ts'
import { handleError } from '@/utils/error'

import { useUpdateTeamTable } from '../../hooks/use-update-table'
import { useUpdateTeamRoleTable } from '../../hooks/use-update-team-role'
import { ChipInput } from '../chip-input'
import { getMultiSelectErrorMessage } from './utils'

interface TeamMemberInviteDialogType {
  setShowInviteDialog: Dispatch<SetStateAction<boolean>>
  showInviteDialog: boolean
}

export type InviteMemberFormValues = z.infer<typeof inviteMemberFormSchema>

const inviteMemberFormSchema = z.object({
  role: z.string(),
  email: z
    .array(emailSchema)
    .min(1, 'Please provide at least one email address'),
  roleIds: z.array(z.number()),
})

const inviteFormValues: InviteMemberFormValues = {
  email: [],
  role: 'Member',
  roleIds: [],
}

export const TeamMemberInviteDialog = ({
  setShowInviteDialog,
  showInviteDialog,
}: TeamMemberInviteDialogType) => {
  const chipInputRef = useRef<HTMLInputElement | null>(null)

  const { inviteMember } = useUpdateTeamTable()
  const { assignRolesToMember } = useUpdateTeamRoleTable()

  const toast = useToast()

  const editForm = useForm<InviteMemberFormValues>({
    mode: 'onChange',
    defaultValues: inviteFormValues,
    resolver: zodResolver(inviteMemberFormSchema),
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = editForm

  const watch = useWatch({ control })
  const role = watch.role

  const handleClose = useCallback(() => {
    setShowInviteDialog(false)
    reset()
  }, [reset, setShowInviteDialog])

  const onSubmit = useCallback(
    async (values: InviteMemberFormValues) => {
      try {
        const unconfirmedEmail = chipInputRef.current?.value

        if (unconfirmedEmail) {
          const validationResult = emailSchema.safeParse(unconfirmedEmail)

          if (validationResult.success) {
            values.email.push(unconfirmedEmail)
          }
        }

        const result: InviteMemberForm[] = values.email.map((email) => ({
          email,
          role: values.role as TeamMemberRole,
        }))

        const data = await inviteMember.mutateAsync(result).then((res) => {
          toast.success('Successfully invited User', { duration: 3000 })

          return res
        })

        data.forEach((user) => {
          assignRolesToMember.mutateAsync({
            memberId: user.id,
            roleIds: values.roleIds,
          })
        })
      } catch (error: unknown) {
        handleError(error, 'Failed to invite team members')
      } finally {
        handleClose()
      }
    },
    [assignRolesToMember, handleClose, inviteMember, toast],
  )

  const emailError = useMemo(
    () => getMultiSelectErrorMessage(errors.email),
    [errors.email],
  )

  return (
    <Dialog
      show={showInviteDialog}
      onClose={handleClose}
      bigDialog={true}
      testId="invite-members-modal"
    >
      <FormProvider {...editForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Header>
            <Dialog.Title>Add Team Members</Dialog.Title>
            <Dialog.Description className="text-center">
              Invite your team members via E-Mail
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Description>
            <div className="flex flex-col gap-5">
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <div className="relative flex flex-col gap-1.5">
                    <label
                      className="text-title text-sm font-medium"
                      htmlFor="email"
                    >
                      Email address
                    </label>
                    <ChipInput
                      {...field}
                      ref={chipInputRef}
                      onChange={field.onChange}
                      placeholder="Email addresses, comma or space separated"
                      data-testid="invite-members-email"
                      inputClassName="min-w-[300px]"
                    />
                    {emailError && (
                      <span className="text-red-500 text-xs">{emailError}</span>
                    )}
                  </div>
                )}
              />

              <Controller
                control={control}
                name="role"
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <label
                      className="text-title text-sm font-medium"
                      htmlFor={field.name}
                    >
                      User Type
                    </label>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="Admin">Admin</SelectItem>
                          <SelectItem value="Member">Member</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />

              <Controller
                control={control}
                name="roleIds"
                render={({ field }) => (
                  <RolesSelector
                    selectedRoleIds={field.value}
                    onChange={field.onChange}
                    label="Roles"
                    name={field.name}
                    disabled={role === TeamMemberRole.ADMIN}
                    disabledReason={
                      role === TeamMemberRole.ADMIN
                        ? 'Admins have all permissions'
                        : undefined
                    }
                    className="col-span-1"
                  />
                )}
              />
            </div>
          </Dialog.Description>
          <Dialog.ClosableFooter onClose={handleClose}>
            <Button
              type="submit"
              variant="default"
              className="w-full"
              isLoading={inviteMember.isLoading}
              testId="invite-members-submit-button"
            >
              Invite
            </Button>
          </Dialog.ClosableFooter>
        </form>
      </FormProvider>
    </Dialog>
  )
}
